import PropTypes from "prop-types";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { REQUIRED_MARK } from "components/BaseForm/fields/fieldClasses";
import {
  PRICE_BASIS,
  PRICE_BASIS_LABELS,
} from "routes/product/pricing/pricing";

const WHOLE_TERM_NEEDS_ONE_TERM =
  "'The whole term' needs exactly 1 allowed term. Remove the others to select it.";
const LOCKED_BY_USE =
  "Price Basis can't be changed because this product is used on invoice lines.";

const basisNotice = (isUsed, termCount) => {
  if (isUsed) return LOCKED_BY_USE;
  if (termCount > 1) return WHOLE_TERM_NEEDS_ONE_TERM;
  return null;
};

const PriceBasisField = ({ isUsed }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const terms = useWatch({ name: "allowed_terms" }) ?? [];
  const isWholeTermBlocked = terms.length !== 1;

  const notice = basisNotice(isUsed, terms.length);

  return (
    <Controller
      name="price_basis"
      control={control}
      render={({ field }) => (
        <Field data-invalid={errors.price_basis ? true : undefined}>
          <FieldLabel htmlFor="price_basis" className={REQUIRED_MARK}>
            What does this price cover?
          </FieldLabel>
          <Select
            value={field.value}
            onValueChange={field.onChange}
            disabled={isUsed}
          >
            <SelectTrigger id="price_basis" className="tw:w-full">
              <SelectValue>{value => PRICE_BASIS_LABELS[value]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={PRICE_BASIS.PER_YEAR}>
                {PRICE_BASIS_LABELS[PRICE_BASIS.PER_YEAR]}
              </SelectItem>
              <SelectItem
                value={PRICE_BASIS.WHOLE_TERM}
                disabled={isWholeTermBlocked}
              >
                {PRICE_BASIS_LABELS[PRICE_BASIS.WHOLE_TERM]}
              </SelectItem>
            </SelectContent>
          </Select>
          {notice && <FieldDescription>{notice}</FieldDescription>}
          <FieldError>{errors.price_basis?.message}</FieldError>
        </Field>
      )}
    />
  );
};

PriceBasisField.propTypes = {
  isUsed: PropTypes.bool.isRequired,
};

export default PriceBasisField;
